import { uiAnalyticsEvents } from "../slices/UIAnalyticsSlice";
import { store } from "../app/store";

const getSessionDetails = () => store.getState().paymentSessionDetails.sessionDetails;

const isSessionActive = (sessionDetails) => {
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  return sessionDetails?.sessionExpiryTimestamp > currentISOTime;
};

const dispatchUiEvent = (uiEvent, eventAttrs) => {
  const sessionDetails = getSessionDetails();
  if (!isSessionActive(sessionDetails)) return;
  store.dispatch(
    uiAnalyticsEvents({
      callerToken: sessionDetails?.token,
      uiEvent,
      ...(eventAttrs && { eventAttrs }),
    })
  );
};

// Click/selection events that carry no attributes.
const createSimpleEvent = (uiEvent) => () => dispatchUiEvent(uiEvent);

// "Entered" field events — optionally tagged eventAttrs.source when the value
// came from browser/OS autofill rather than a real keystroke.
const createEnteredEvent = (uiEvent) => (source) =>
  dispatchUiEvent(uiEvent, { ...(source && { source }) });

export const checkoutLoadedAnalytics = (status, eventData) => {
  const sessionDetails = getSessionDetails();
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  if (
    Object.keys(sessionDetails)?.length &&
    (sessionDetails?.status === "PENDING" ||
      sessionDetails?.status === "FAILED")
  ) {
    if (sessionDetails?.sessionExpiryTimestamp > currentISOTime) {
      const payload = {
        callerToken: sessionDetails?.token,
        uiEvent: "CHECKOUT_LOADED",
        eventAttrs: {
          status,
          ...(eventData || {}),
        },
      };

      store.dispatch(uiAnalyticsEvents(payload));
    }
  }
};

// Fired before/without a loaded session (e.g. the initial load itself failed), so
// the caller token is passed in rather than read off store state.
export const checkoutLoadedWithErrorAnalytics = (token, status, extraInfo) => {
  const payload = {
    callerToken: token,
    uiEvent: "CHECKOUT_LOADED",
    eventAttrs: {
      status,
      ...(typeof extraInfo === "object" && extraInfo !== null ? extraInfo : { info: extraInfo }),
    },
  };

  store.dispatch(uiAnalyticsEvents(payload));
};

export const addressAnalyticsEvent = createSimpleEvent("ADDRESS_UPDATED");

export const categorySelectedEvent = (paymentType) => {
  const sessionDetails = getSessionDetails();
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  if (sessionDetails?.sessionExpiryTimestamp > currentISOTime) {
    const payload = {
      callerToken: sessionDetails?.token,
      uiEvent: "PAYMENT_CATEGORY_SELECTED",
      eventAttrs: {
        paymentType: paymentType,
      },
    };

    store.dispatch(uiAnalyticsEvents(payload));
  }
};

export const methodSelectedEvent = (paymentType, paymentSubType) => {
  const sessionDetails = getSessionDetails();
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  if (sessionDetails?.sessionExpiryTimestamp > currentISOTime) {
    const payload = {
      callerToken: sessionDetails?.token,
      uiEvent: "PAYMENT_METHOD_SELECTED",
      eventAttrs: {
        paymentType: paymentType,
        paymentSubType: paymentSubType,
      },
    };

    store.dispatch(uiAnalyticsEvents(payload));
  }
};

export const paymentInitiatedEvent = (paymentType, paymentSubType, errorInfo) => {
  const sessionDetails = getSessionDetails();
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  if (sessionDetails?.sessionExpiryTimestamp > currentISOTime) {
    const payload = {
      callerToken: sessionDetails?.token,
      uiEvent: "PAYMENT_INITIATED",
      eventAttrs: {
        paymentType: paymentType,
        paymentSubType: paymentSubType,

      },
    };
    if (errorInfo) {
      payload.eventAttrs.errorInfo = errorInfo;
    }

    store.dispatch(uiAnalyticsEvents(payload));
  }
};
export const checkoutCrashEvent = (message, stack) => {
  const sessionDetails = getSessionDetails();
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  if (sessionDetails?.sessionExpiryTimestamp > currentISOTime) {
    const payload = {
      callerToken: sessionDetails?.token,
      uiEvent: "WEB_CHECKOUT_CRASH",
      eventAttrs: {
        type: "runtime_error",
        message: message || null,
        stack: stack || null,
      },
    };
    store.dispatch(uiAnalyticsEvents(payload));
  }
};

export const autoRetryAttemptedEvent = createSimpleEvent("AUTO_RETRY_ATTEMPTED");
export const autoRetryCancelledEvent = createSimpleEvent("AUTO_RETRY_CANCELLED");
export const paymentCancelledEvent = createSimpleEvent("PAYMENT_CANCELLED");

export const fullNameSelectedEvent = createSimpleEvent("FULL_NAME_SELECTED");
export const fullNameEnteredEvent = createEnteredEvent("FULL_NAME_ENTERED");

export const mobileNumberSelectedEvent = createSimpleEvent("MOBILE_NUMBER_SELECTED");
export const mobileNumberEnteredEvent = createEnteredEvent("MOBILE_NUMBER_ENTERED");

// Shopper login (mobile + OTP) flow
export const loginNumberSelectedEvent = createSimpleEvent("LOGIN_NUMBER_SELECTED");
export const loginNumberEnteredEvent = createEnteredEvent("LOGIN_NUMBER_ENTERED");

export const keepSignedInSelectedEvent = createSimpleEvent("KEEP_SIGNED_IN_SELECTED");
export const keepSignedInRemovedEvent = createSimpleEvent("KEEP_SIGNED_IN_REMOVED");

export const otpRequestedEvent = createSimpleEvent("OTP_REQUESTED");
export const resendOtpEvent = createSimpleEvent("RESEND_OTP");

export const loginOtpSelectedEvent = createSimpleEvent("LOGIN_OTP_SELECTED");
export const loginOtpEnteredEvent = createEnteredEvent("LOGIN_OTP_ENTERED");

export const loginCompleteEvent = createSimpleEvent("LOGIN_COMPLETE");
// Login attempt failed — OTP could not be sent, or the entered OTP was rejected.
// Optional `reason` carries the backend error message for context.
export const loginFailedEvent = (reason) =>
  dispatchUiEvent("LOGIN_FAILED", { ...(reason && { reason }) });

export const guestCheckoutLoginEvent = createSimpleEvent("GUEST_CHECKOUT_LOGIN");

export const emailSelectedEvent = createSimpleEvent("EMAIL_SELECTED");
export const emailEnteredEvent = createEnteredEvent("EMAIL_ENTERED");

export const countrySelectedEvent = createSimpleEvent("COUNTRY_SELECTED");
export const countryEnteredEvent = createEnteredEvent("COUNTRY_ENTERED");

export const zipCodeSelectedEvent = createSimpleEvent("ZIP_CODE_SELECTED");
export const zipCodeEnteredEvent = createEnteredEvent("ZIP_CODE_ENTERED");

export const stateSelectedEvent = createSimpleEvent("STATE_SELECTED");
export const stateEnteredEvent = createEnteredEvent("STATE_ENTERED");

export const citySelectedEvent = createSimpleEvent("CITY_SELECTED");
export const cityEnteredEvent = createEnteredEvent("CITY_ENTERED");

export const houseNumberSelectedEvent = createSimpleEvent("HOUSE_NUMBER_SELECTED");
export const houseNumberEnteredEvent = createEnteredEvent("HOUSE_NUMBER_ENTERED");

export const areaSectorSelectedEvent = createSimpleEvent("AREA_SECTOR_SELECTED");
export const areaSectorEnteredEvent = createEnteredEvent("AREA_SECTOR_ENTERED");

export const offerListSelectedEvent = createSimpleEvent("OFFER_LIST_SELECTED");
export const offerAppliedEvent = createSimpleEvent("OFFER_APPLIED");

export const offerInputSelectedEvent = createSimpleEvent("OFFER_INPUT_SELECTED");
export const offerInputEnteredEvent = createEnteredEvent("OFFER_INPUT_ENTERED");

export const offerRemovedEvent = createSimpleEvent("OFFER_REMOVED");
export const offerListClosedEvent = createSimpleEvent("OFFER_LIST_CLOSED");

export const reenterDetailsOfferEnteredEvent = createEnteredEvent("REENTER_DETAILS_OFFER_ENTERED");
export const continueWithoutOfferEnteredEvent = createEnteredEvent("CONTINUE_WITHOUT_OFFER_ENTERED");
export const surchargeInformationEnteredEvent = createEnteredEvent("SURCHARGE_INFORMATION_ENTERED");

export const viewSurchargeEvent = createSimpleEvent("VIEW_SURCHARGE");
export const hideSurchargeEvent = createSimpleEvent("HIDE_SURCHARGE");

export const emiCategorySelectedEvent = (category) =>
  dispatchUiEvent("EMI_CATEGORY_SELECTED", { category });

export const emiDropdownSelectedEvent = createSimpleEvent("EMI_DROPDOWN_SELECTED");
export const emiDropdownEnteredEvent = createEnteredEvent("EMI_DROPDOWN_ENTERED");

export const cardNumberSelectedEvent = createSimpleEvent("CARD_NUMBER_SELECTED");
export const cardNumberEnteredEvent = createEnteredEvent("CARD_NUMBER_ENTERED");

export const cardHolderNameSelectedEvent = createSimpleEvent("CARD_HOLDER_NAME_SELECTED");
export const cardHolderNameEnteredEvent = createEnteredEvent("CARD_HOLDER_NAME_ENTERED");

export const cardExpiryDateSelectedEvent = createSimpleEvent("CARD_EXPIRY_DATE_SELECTED");
export const cardExpiryDateEnteredEvent = createEnteredEvent("CARD_EXPIRY_DATE_ENTERED");

export const cardCvvSelectedEvent = createSimpleEvent("CARD_CVV_SELECTED");
export const cardCvvEnteredEvent = createEnteredEvent("CARD_CVV_ENTERED");

export const retryButtonClickedEvent = createSimpleEvent("RETRY_BUTTON_CLICKED");
export const retryModalClosedEvent = createSimpleEvent("RETRY_MODAL_CLOSED");
export const stopAutoRetryClickedEvent = createSimpleEvent("STOP_AUTO_RETRY_CLICKED");
export const autoRetryModalClosedEvent = createSimpleEvent("AUTO_RETRY_MODAL_CLOSED");

export const languageClickedEvent = createSimpleEvent("LANGUAGE_CLICKED");

export const languageSelectedEvent = (value) => {
  const sessionDetails = getSessionDetails();
  const currentTimestamp = new Date().toUTCString();
  const currentISOTime = new Date(currentTimestamp).toISOString();
  if (sessionDetails?.sessionExpiryTimestamp > currentISOTime) {
    const payload = {
      callerToken: sessionDetails?.token,
      uiEvent: "LANGUAGE_SELECTED",
      eventAttrs: {
        language: value,
      },
    };
    store.dispatch(uiAnalyticsEvents(payload));
  }
};

export const backButtonClickedEvent = createSimpleEvent("CANCEL_ORDER_MODAL_OPENED");
export const backButtonModalConfirmedEvent = createSimpleEvent("CANCEL_ORDER_BUTTON_CLICKED");
export const backButtonModalCancelledEvent = createSimpleEvent("CANCEL_ORDER_MODAL_CLOSED");

export const nativeOtpEnteredEvent = createSimpleEvent("NATIVE_OTP_ENTERED");
export const nativeOtpScreenShownEvent = createSimpleEvent("NATIVE_OTP_SCREEN_SHOWN");
export const nativeOtpSubmittedEvent = createSimpleEvent("NATIVE_OTP_SUBMITTED");
export const nativeOtpResendEvent = createSimpleEvent("NATIVE_OTP_RESEND");
export const nativeOtpCancelledEvent = createSimpleEvent("NATIVE_OTP_CANCELLED");
export const nativeOtpBankFallbackEvent = createSimpleEvent("NATIVE_OTP_BANK_FALLBACK");
export const nativeOtpExpiredEvent = createSimpleEvent("NATIVE_OTP_EXPIRED");
export const nativeOtpCancelConfirmShownEvent = createSimpleEvent("NATIVE_OTP_CANCEL_CONFIRM_SHOWN");

export const deliveryAddressEditEvent = createSimpleEvent("DELIVERY_ADDRESS_EDIT");

export const customFieldSelectedEvent = (fieldName, code) =>
  dispatchUiEvent("CUSTOM_FIELD_SELECTED", { fieldName, ...(code && { code }) });

export const customFieldEnteredEvent = (fieldName, code, source) =>
  dispatchUiEvent("CUSTOM_FIELD_ENTERED", { fieldName, ...(code && { code }), ...(source && { source }) });

export const showLoginPageEvent = createSimpleEvent("SHOW_LOGIN_PAGE");

export const logoutModalOpenedEvent = createSimpleEvent("LOGOUT_MODAL_OPENED");
export const logoutModalClosedEvent = createSimpleEvent("LOGOUT_MODAL_CLOSED");
export const logoutCompleteEvent = createSimpleEvent("LOGOUT_COMPLETE");

export const termsAndConditionsClickedEvent = createSimpleEvent("TERMS_AND_CONDITIONS_CLICKED");
export const privacyPolicyClickedEvent = createSimpleEvent("PRIVACY_POLICY_CLICKED");

export const onDemandAmountSelectedEvent = createSimpleEvent("ON_DEMAND_AMOUNT_SELECTED");
export const onDemandAmountEnteredEvent = createEnteredEvent("ON_DEMAND_AMOUNT_ENTERED");
export const onDemandProceedEvent = createSimpleEvent("ON_DEMAND_PROCEED");
export const onDemandAmountEditedEvent = createSimpleEvent("ON_DEMAND_AMOUNT_EDITED");

export const viewAllSavedCardsEvent = createSimpleEvent("VIEW_ALL_SAVED_CARDS");
export const closeSavedCardModalEvent = createSimpleEvent("CLOSE_SAVED_CARD_MODAL");
export const deleteSavedCardModalClickedEvent = createSimpleEvent("DELETE_SAVED_CARD_MODAL_CLICKED");
export const deleteSavedCardModalClosedEvent = createSimpleEvent("DELETE_SAVED_CARD_MODAL_CLOSED");
export const saveCardRemovedEvent = createSimpleEvent("SAVE_CARD_REMOVED");
export const addNewSavedCardEvent = createSimpleEvent("ADD_NEW_SAVED_CARD");
export const saveCardBackButtonClickedEvent = createSimpleEvent("SAVE_CARD_BACK_BUTTON_CLICKED");
export const knowMoreButtonClickedEvent = createSimpleEvent("KNOW_MORE_BUTTON_CLICKED");
export const knowMoreModalClosedEvent = createSimpleEvent("KNOW_MORE_MODAL_CLOSED");
export const saveCardOptionSelectedEvent = createSimpleEvent("SAVE_CARD_OPTION_SELECTED");
export const saveCardOptionRemovedEvent = createSimpleEvent("SAVE_CARD_OPTION_REMOVED");
